import { useMemo, useState } from "react";
import { Col, Form, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";
import FormInput from "../../common/FormInput";
import TableContainer from "Components/Common/TableContainer";
import TableContainerReactTable from "Components/Common/TableContainerReactTable";
import SelectInput from "../../common/SelectInput";

export const ApplyTemplateModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [activeTab, setActiveTab] = useState<string>('1');

  // Dummy templates with matrices
  type Template = {
    templateId: number;
    templateName: string;
    sizes: string[];
    colors: string[];
    matrix: Record<string, Record<string, boolean>>; // size -> color -> enabled
  };

  const templates: Template[] = [
    {
      templateId: 1,
      templateName: 'D SHOES STANDARD',
      sizes: ['5', '5.5', '6', '7.5', '8', '9', '10'],
      colors: ['BLACK', 'GREEN', 'RED'],
      matrix: {
        '5':    { BLACK: true,  GREEN: false, RED: false },
        '5.5':  { BLACK: true,  GREEN: false, RED: false },
        '6':    { BLACK: true,  GREEN: false, RED: false },
        '7.5':  { BLACK: false, GREEN: true,  RED: false },
        '8':    { BLACK: false, GREEN: true,  RED: false },
        '9':    { BLACK: false, GREEN: false, RED: true  },
        '10':   { BLACK: true,  GREEN: false, RED: true  }
      }
    },
    {
      templateId: 2,
      templateName: 'Indian Team Jersey',
      sizes: ['S', 'M', 'L', 'XL'],
      colors: ['BLUE', 'WHITE'],
      matrix: {
        S:  { BLUE: true,  WHITE: false },
        M:  { BLUE: true,  WHITE: true  },
        L:  { BLUE: false, WHITE: true  },
        XL: { BLUE: true,  WHITE: true  }
      }
    },
    {
      templateId: 3,
      templateName: 'Standard Car',
      sizes: ['Base', 'Sport', 'Luxury'],
      colors: ['BLACK', 'SILVER', 'RED'],
      matrix: {
        Base:   { BLACK: true,  SILVER: true,  RED: false },
        Sport:  { BLACK: false, SILVER: true,  RED: true  },
        Luxury: { BLACK: true,  SILVER: false, RED: true  }
      }
    }
  ];

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(1);
  const selectedTemplate = useMemo(() => templates.find(t => t.templateId === selectedTemplateId) ?? templates[0], [selectedTemplateId]);

  //table columns
  const columns = useMemo(() => [
    {
      header: 'Pick', accessorKey: 'pick', enableColumnFilter: false, cell: (cell: any) => (<div>
        <input
          type="radio"
          name="template-pick"
          checked={cell.row.original.templateId === selectedTemplateId}
          onChange={() => setSelectedTemplateId(cell.row.original.templateId)}
          className="form-check-input"
        />
      </div>)
    },
    { header: 'Template Id', accessorKey: 'templateId', enableColumnFilter: false, cell: (cell: any) => (<div>{cell.getValue()}</div>) },
    { header: 'Template Name', accessorKey: 'templateName', enableColumnFilter: false, cell: (cell: any) => (<div>{cell.getValue()}</div>) },
  ], [selectedTemplateId]);

  //table columns
  const columns2 = useMemo(() => {
    const base = [
      { header: 'Colour', accessorKey: 'color', enableColumnFilter: false, cell: (cell: any) => (<div className="text-uppercase fw-medium">{cell.getValue()}</div>) },
    ];
    const sizeCols = (selectedTemplate?.sizes ?? []).map(size => ({
      header: size,
      accessorKey: size,
      enableColumnFilter: false,
      cell: ({ getValue }: any) => (
        <div className="d-flex justify-content-center">
          <input type="checkbox" className="form-check-input" checked={!!getValue()} readOnly />
        </div>
      )
    }));
    return [...base, ...sizeCols];
  }, [selectedTemplate]);

  //table data
  const topData = useMemo(() => templates.map(t => ({ templateId: t.templateId, templateName: t.templateName })), []);
  const bottomData = useMemo(() => {
    const tpl = selectedTemplate;
    if (!tpl) return [] as any[];
    return tpl.colors.map(color => {
      const row: any = { color };
      tpl.sizes.forEach(size => {
        row[size] = tpl.matrix[size]?.[color] ?? false;
      });
      return row;
    });
  }, [selectedTemplate]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        Search Templates
      </ModalHeader>

      <ModalBody>
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink className={`${activeTab === '1' ? 'active' : ''}`} tabIndex={0} aria-label="Search Tab">
              Search
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab} className="text-muted">
          <TabPane tabId="1">
            <Row className="mb-2 align-items-center gap-2">
              <Col md={4}>
                <SelectInput
                  formData={{ searchField: 'templateId' }}
                  setFormData={() => { }}
                  label="Search"
                  id="searchField"
                  options={['templateId']}
                />
              </Col>
              <Col md={4}>
                <FormInput
                  id="searchValue"
                  label="For Value"
                  value={'value'}
                  onChange={() => { }}
                />
              </Col>
              <Col md={2} className="d-flex gap-2">
                <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }} onClick={() => { }}>Search</button>
                <button className="btn btn-soft-warning btn-sm" style={{ minWidth: 80 }} onClick={() => { }}>Clear</button>
              </Col>
            </Row>
            <Row>
              <Col xs={4}>
                <SelectInput
                  formData={{ templateAttribute: 'Apply Matrix' }}
                  setFormData={() => { }}
                  label="Template Attribute to Apply"
                  id="templateAttribute"
                  options={['Apply Matrix']}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <div className="table-responsive mb-3">
                  <TableContainerReactTable
                    isGlobalFilter={false}
                    columns={columns}
                    data={topData}
                    hidePagination={true}
                    verticalLines={true}
                    theadClass="table-light"
                    thClass="px-3 py-2"
                    divClass="mb-0"
                    tableClass="table-centered align-middle table-nowrap mb-0"
                  />
                </div>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col xs={12}>
                <div className="text-muted small mb-2">Size</div>
                <div className="p-2 border rounded">
                  <div className="table-responsive mb-1">
                  <TableContainerReactTable
                    isGlobalFilter={false}
                    columns={columns2}
                    data={bottomData}
                    hidePagination={true}
                    verticalLines={true}
                    theadClass="table-light"
                    thClass="px-3 py-2"
                    divClass="mb-0"
                    tableClass="table-centered align-middle table-nowrap mb-0"
                  />
                  </div>
                </div>
              </Col>
            </Row>

          </TabPane>
        </TabContent>
      </ModalBody>

      <ModalFooter className="d-flex justify-content-end gap-2">
        <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }} onClick={() => { }}>Apply</button>
        <button className="btn btn-soft-info btn-sm" style={{ minWidth: 80 }} onClick={() => { }}>Apply & Create Products</button>
        <button className="btn btn-soft-dark btn-sm" style={{ minWidth: 80 }} onClick={() => { }}>Cancel</button>
      </ModalFooter>
    </Modal>
  )
}